import React, { useCallback, useState } from "react";
import "./admin.css";

// ── Types ─────────────────────────────────────────────────────────────────────

type AreaUnit = "sqft" | "sqm";
type DiscountType = "none" | "amount" | "percent";

interface Inputs {
  area: string;
  areaUnit: AreaUnit;
  rate: string;
  discountType: DiscountType;
  discountValue: string;
  gstRate: string;
}

interface Results {
  grossCost: number;
  discountAmount: number;
  costAfterDiscount: number;
  gstAmount: number;
  totalWithGst: number;
  effectiveArea: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SQM_TO_SQFT = 10.7639;
const INITIAL: Inputs = {
  area: "",
  areaUnit: "sqft",
  rate: "",
  discountType: "none",
  discountValue: "",
  gstRate: "18",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const toNum = (s: string) => {
  const n = parseFloat(String(s).replace(/,/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);

const fmtNum = (n: number) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);

const fmtArea = (n: number, unit: AreaUnit) =>
  `${fmtNum(n)} ${unit === "sqft" ? "sq.ft" : "sq.m"}`;

const calc = (inputs: Inputs): Results => {
  const rawArea = toNum(inputs.area);
  const effectiveArea =
    inputs.areaUnit === "sqm" ? rawArea * SQM_TO_SQFT : rawArea;
  const rate = toNum(inputs.rate);
  const gstRate = toNum(inputs.gstRate);
  const grossCost = effectiveArea * rate;

  let discountAmount = 0;
  if (inputs.discountType === "amount") {
    discountAmount = Math.min(toNum(inputs.discountValue), grossCost);
  } else if (inputs.discountType === "percent") {
    const pct = Math.min(toNum(inputs.discountValue), 100);
    discountAmount = (grossCost * pct) / 100;
  }

  const costAfterDiscount = grossCost - discountAmount;
  const gstAmount = (costAfterDiscount * gstRate) / 100;
  const totalWithGst = costAfterDiscount + gstAmount;

  return { grossCost, discountAmount, costAfterDiscount, gstAmount, totalWithGst, effectiveArea };
};

// ── Styled sub-components ─────────────────────────────────────────────────────

const Desc: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: "block",
      fontSize: "0.7rem",
      color: "#94a3b8",
      fontWeight: 400,
      marginTop: 1,
      lineHeight: 1.3,
    }}
  >
    {children}
  </span>
);

const MetricCard: React.FC<{
  icon: string;
  label: string;
  desc: string;
  value: string;
  accent: string;
  bg: string;
  border: string;
  large?: boolean;
}> = ({ icon, label, desc, value, accent, bg, border, large }) => (
  <div
    style={{
      background: bg,
      border: `1.5px solid ${border}`,
      borderRadius: 10,
      padding: large ? "12px 16px" : "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 8,
    }}
  >
    <div
      style={{
        width: large ? 40 : 34,
        height: large ? 40 : 34,
        borderRadius: 9,
        background: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <i className={icon} style={{ fontSize: large ? 18 : 15, color: "#fff" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: "0.7rem",
          fontWeight: 600,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 1,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: large ? "1.25rem" : "1.05rem",
          fontWeight: 700,
          color: accent,
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 1 }}>{desc}</div>
    </div>
  </div>
);

const SectionHead: React.FC<{ icon: string; title: string; color: string }> = ({ icon, title, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 14,
      paddingBottom: 10,
      borderBottom: `2px solid ${color}22`,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 7,
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <i className={icon} style={{ color: "#fff", fontSize: 13 }} />
    </div>
    <span
      style={{
        fontWeight: 700,
        fontSize: "0.78rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#475569",
      }}
    >
      {title}
    </span>
  </div>
);

const FieldRow: React.FC<{
  label: string;
  desc: string;
  accent?: string;
  children: React.ReactNode;
}> = ({ label, desc, accent = "#6366f1", children }) => (
  <div
    style={{
      borderLeft: `3px solid ${accent}`,
      paddingLeft: 10,
      marginBottom: 14,
    }}
  >
    <label
      className="form-label mb-0"
      style={{ fontWeight: 600, fontSize: "0.83rem", color: "#334155", marginBottom: 1 }}
    >
      {label}
    </label>
    <Desc>{desc}</Desc>
    <div style={{ marginTop: 5 }}>{children}</div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const CostEstimator: React.FC = () => {
  const [inputs, setInputs] = useState<Inputs>(INITIAL);

  const set = useCallback(
    <K extends keyof Inputs>(key: K, value: Inputs[K]) =>
      setInputs((prev) => ({ ...prev, [key]: value })),
    []
  );

  const reset = () => setInputs(INITIAL);

  const r = calc(inputs);
  const hasValues = toNum(inputs.area) > 0 && toNum(inputs.rate) > 0;
  const discountPct =
    r.grossCost > 0
      ? ((r.discountAmount / r.grossCost) * 100).toFixed(2)
      : "0.00";
  const finalAmount = toNum(inputs.gstRate) > 0 ? r.totalWithGst : r.costAfterDiscount;

  return (
    <section className="admin-page">
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #134e4a 0%, #0d9488 60%, #14b8a6 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          boxShadow: "0 3px 16px #0d948833",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className="bi bi-calculator-fill" style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "1.15rem",
                margin: 0,
                letterSpacing: "-0.01em",
              }}
            >
              Project Cost Estimator
            </h2>
            <p style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.78rem", margin: 0 }}>
              Enter area, rate &amp; discount — results update in real time
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: 7,
            padding: "6px 14px",
            fontSize: "0.8rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="bi bi-arrow-counterclockwise" />
          Reset
        </button>
      </div>

      <div className="row g-3">
        {/* ── INPUT PANEL ──────────────────────────────────────────────────── */}
        <div className="col-lg-5">
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1.5px solid #e2e8f0",
              padding: "18px 18px 14px",
              boxShadow: "0 1px 8px #0000000a",
            }}
          >
            <SectionHead icon="bi bi-sliders2" title="Input Parameters" color="#6366f1" />

            {/* Area of Land */}
            <FieldRow
              label="Area of Land *"
              desc="Total built-up or plot area in sq.ft or sq.m"
              accent="#6366f1"
            >
              <div className="input-group input-group-sm">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="form-control form-control-sm"
                  value={inputs.area}
                  onChange={(e) => set("area", e.target.value)}
                  title="Area of land"
                  aria-label="Area of land"
                  style={{ borderColor: "#c7d2fe", boxShadow: "none" }}
                />
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: 80, borderColor: "#c7d2fe", background: "#eef2ff", fontWeight: 600, fontSize: "0.8rem", boxShadow: "none" }}
                  value={inputs.areaUnit}
                  onChange={(e) => set("areaUnit", e.target.value as AreaUnit)}
                  title="Unit of measurement"
                  aria-label="Area unit"
                >
                  <option value="sqft">sq.ft</option>
                  <option value="sqm">sq.m</option>
                </select>
              </div>
              {inputs.areaUnit === "sqm" && toNum(inputs.area) > 0 && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: "0.72rem",
                    color: "#6366f1",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <i className="bi bi-arrow-right-circle-fill" />
                  = {fmtArea(r.effectiveArea, "sqft")} — used in calculation
                </div>
              )}
            </FieldRow>

            {/* Rate of Construction */}
            <FieldRow
              label="Rate of Construction *"
              desc="Cost per sq.ft charged for development"
              accent="#0ea5e9"
            >
              <div className="input-group input-group-sm">
                <span
                  className="input-group-text"
                  style={{ background: "#e0f2fe", color: "#0369a1", fontWeight: 700, borderColor: "#bae6fd", fontSize: "0.85rem" }}
                >
                  ₹
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="form-control form-control-sm"
                  value={inputs.rate}
                  onChange={(e) => set("rate", e.target.value)}
                  title="Rate of construction per sq.ft"
                  aria-label="Rate of construction per square foot"
                  style={{ borderColor: "#bae6fd", boxShadow: "none" }}
                />
                <span
                  className="input-group-text"
                  style={{ background: "#e0f2fe", color: "#64748b", fontSize: "0.75rem", borderColor: "#bae6fd" }}
                >
                  / sq.ft
                </span>
              </div>
            </FieldRow>

            {/* Separator */}
            <div style={{ borderTop: "1.5px dashed #e2e8f0", margin: "4px 0 14px" }} />

            {/* Discount */}
            <FieldRow
              label="Discount"
              desc="Flat rupee deduction or percentage off gross cost"
              accent="#f59e0b"
            >
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginBottom: inputs.discountType !== "none" ? 8 : 0,
                  flexWrap: "wrap",
                }}
              >
                {(
                  [
                    { value: "none",    label: "None",     icon: "bi-x-circle" },
                    { value: "amount",  label: "₹ Amount", icon: "bi-currency-rupee" },
                    { value: "percent", label: "% Off",    icon: "bi-percent" },
                  ] as { value: DiscountType; label: string; icon: string }[]
                ).map(({ value, label, icon }) => {
                  const active = inputs.discountType === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        set("discountType", value);
                        set("discountValue", "");
                      }}
                      style={{
                        padding: "4px 11px",
                        borderRadius: 6,
                        border: active ? "2px solid #f59e0b" : "1.5px solid #e2e8f0",
                        background: active ? "#fffbeb" : "#f8fafc",
                        color: active ? "#b45309" : "#64748b",
                        fontWeight: active ? 700 : 500,
                        fontSize: "0.76rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        transition: "all 0.15s",
                      }}
                    >
                      <i className={`bi ${icon}`} />
                      {label}
                    </button>
                  );
                })}
              </div>

              {inputs.discountType !== "none" && (
                <div className="input-group input-group-sm">
                  {inputs.discountType === "amount" && (
                    <span
                      className="input-group-text"
                      style={{ background: "#fef3c7", color: "#92400e", fontWeight: 700, borderColor: "#fcd34d", fontSize: "0.85rem" }}
                    >
                      ₹
                    </span>
                  )}
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="form-control form-control-sm"
                    value={inputs.discountValue}
                    onChange={(e) => set("discountValue", e.target.value)}
                    title={inputs.discountType === "amount" ? "Discount amount in rupees" : "Discount percentage"}
                    aria-label={inputs.discountType === "amount" ? "Discount amount" : "Discount percentage"}
                    style={{ borderColor: "#fcd34d", boxShadow: "none" }}
                  />
                  {inputs.discountType === "percent" && (
                    <span
                      className="input-group-text"
                      style={{ background: "#fef3c7", color: "#92400e", fontWeight: 700, borderColor: "#fcd34d", fontSize: "0.85rem" }}
                    >
                      %
                    </span>
                  )}
                </div>
              )}
            </FieldRow>

            {/* GST Rate */}
            <FieldRow
              label="GST Rate"
              desc="Tax on discounted amount — enter 0 to exclude GST"
              accent="#10b981"
            >
              <div className="input-group input-group-sm" style={{ maxWidth: 150 }}>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  className="form-control form-control-sm"
                  value={inputs.gstRate}
                  onChange={(e) => set("gstRate", e.target.value)}
                  title="GST rate in percentage"
                  aria-label="GST rate percentage"
                  style={{ borderColor: "#a7f3d0", boxShadow: "none" }}
                />
                <span
                  className="input-group-text"
                  style={{ background: "#d1fae5", color: "#065f46", fontWeight: 700, borderColor: "#a7f3d0", fontSize: "0.85rem" }}
                >
                  %
                </span>
              </div>
            </FieldRow>
          </div>
        </div>

        {/* ── RESULT PANEL ─────────────────────────────────────────────────── */}
        <div className="col-lg-7">
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1.5px solid #e2e8f0",
              padding: "18px 18px 14px",
              boxShadow: "0 1px 8px #0000000a",
            }}
          >
            <SectionHead icon="bi bi-receipt-cutoff" title="Cost Breakdown" color="#2563eb" />

            {/* Formula pill */}
            {hasValues ? (
              <div
                style={{
                  background: "linear-gradient(90deg, #eff6ff, #f0fdf4)",
                  border: "1.5px solid #bfdbfe",
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 12,
                  fontSize: "0.8rem",
                  color: "#1e40af",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className="bi bi-lightning-fill" style={{ color: "#f59e0b" }} />
                <span>
                  <strong>{fmtArea(r.effectiveArea, "sqft")}</strong>
                  <span style={{ color: "#64748b", margin: "0 5px" }}>×</span>
                  <strong>₹{fmtNum(toNum(inputs.rate))}/sq.ft</strong>
                  <span style={{ color: "#64748b", margin: "0 5px" }}>=</span>
                  <strong style={{ color: "#2563eb" }}>{fmt(r.grossCost)}</strong>
                </span>
              </div>
            ) : (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1.5px dashed #cbd5e1",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 12,
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                }}
              >
                <i className="bi bi-arrow-left-circle me-2" />
                Enter area and rate on the left to see your estimate
              </div>
            )}

            {/* Metric cards — top row */}
            <div className="row g-2 mb-0">
              <div className="col-md-6">
                <MetricCard
                  icon="bi bi-buildings-fill"
                  label="Gross Cost"
                  desc="Area × Rate · before any deduction"
                  value={hasValues ? fmt(r.grossCost) : "—"}
                  accent="#2563eb"
                  bg="#eff6ff"
                  border="#bfdbfe"
                />
              </div>
              <div className="col-md-6">
                <MetricCard
                  icon="bi bi-tag-fill"
                  label={`Discount${inputs.discountType === "percent" ? ` · ${toNum(inputs.discountValue).toFixed(1)}%` : ""}`}
                  desc={
                    hasValues && r.discountAmount > 0
                      ? `${discountPct}% off gross cost`
                      : "No deduction applied"
                  }
                  value={
                    hasValues && r.discountAmount > 0
                      ? `– ${fmt(r.discountAmount)}`
                      : hasValues
                      ? "₹ 0"
                      : "—"
                  }
                  accent="#dc2626"
                  bg="#fff1f2"
                  border="#fecaca"
                />
              </div>
            </div>

            {/* After discount */}
            <MetricCard
              icon="bi bi-check2-circle"
              label="Cost After Discount"
              desc="Gross cost minus the discount amount"
              value={hasValues ? fmt(r.costAfterDiscount) : "—"}
              accent="#7c3aed"
              bg="#f5f3ff"
              border="#ddd6fe"
            />

            {/* GST row */}
            {toNum(inputs.gstRate) > 0 && (
              <div className="row g-2 mb-0">
                <div className="col-md-5">
                  <MetricCard
                    icon="bi bi-percent"
                    label={`GST @ ${toNum(inputs.gstRate)}%`}
                    desc="Tax on discounted amount"
                    value={hasValues ? `+ ${fmt(r.gstAmount)}` : "—"}
                    accent="#d97706"
                    bg="#fffbeb"
                    border="#fde68a"
                  />
                </div>
                <div className="col-md-7">
                  <div
                    style={{
                      background: "linear-gradient(135deg, #064e3b, #059669)",
                      borderRadius: 10,
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      height: "100%",
                      boxShadow: "0 3px 12px #05966922",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 9,
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-currency-rupee" style={{ fontSize: 20, color: "#fff" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#a7f3d0", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>
                        Total Payable (Incl. GST)
                      </div>
                      <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#fff", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
                        {hasValues ? fmt(finalAmount) : "—"}
                      </div>
                      <div style={{ fontSize: "0.68rem", color: "#6ee7b7", marginTop: 2 }}>
                        Final amount payable by customer
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Final total when GST = 0 */}
            {toNum(inputs.gstRate) === 0 && (
              <div
                style={{
                  background: "linear-gradient(135deg, #064e3b, #059669)",
                  borderRadius: 10,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  boxShadow: "0 3px 12px #05966922",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i className="bi bi-currency-rupee" style={{ fontSize: 20, color: "#fff" }} />
                </div>
                <div>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#a7f3d0", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>
                    Final Estimated Cost (GST Excluded)
                  </div>
                  <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#fff", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
                    {hasValues ? fmt(r.costAfterDiscount) : "—"}
                  </div>
                  <div style={{ fontSize: "0.68rem", color: "#6ee7b7", marginTop: 2 }}>
                    Final amount payable · GST not applicable
                  </div>
                </div>
              </div>
            )}

            {/* Summary breakdown table */}
            {hasValues && (
              <div
                style={{
                  marginTop: 14,
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "7px 14px",
                    borderBottom: "1.5px solid #e2e8f0",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                  }}
                >
                  <i className="bi bi-list-ul me-2" />
                  Full Breakdown
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: "0.84rem" }}>
                  <tbody>
                    {[
                      {
                        label: "Area",
                        value: fmtArea(toNum(inputs.area), inputs.areaUnit) +
                          (inputs.areaUnit === "sqm" ? ` (${fmtArea(r.effectiveArea, "sqft")})` : ""),
                        color: "#334155",
                        bold: false,
                      },
                      {
                        label: "Rate",
                        value: `₹${fmtNum(toNum(inputs.rate))} / sq.ft`,
                        color: "#334155",
                        bold: false,
                      },
                      {
                        label: "Gross Cost",
                        value: fmt(r.grossCost),
                        color: "#2563eb",
                        bold: true,
                      },
                      ...(r.discountAmount > 0
                        ? [
                            {
                              label: `Discount${inputs.discountType === "percent" ? ` (${toNum(inputs.discountValue)}%)` : ""}`,
                              value: `– ${fmt(r.discountAmount)}`,
                              color: "#dc2626",
                              bold: true,
                            },
                          ]
                        : []),
                      {
                        label: "After Discount",
                        value: fmt(r.costAfterDiscount),
                        color: "#7c3aed",
                        bold: true,
                      },
                      ...(toNum(inputs.gstRate) > 0
                        ? [
                            {
                              label: `GST (${toNum(inputs.gstRate)}%)`,
                              value: `+ ${fmt(r.gstAmount)}`,
                              color: "#d97706",
                              bold: false,
                            },
                          ]
                        : []),
                    ].map((row, i) => (
                      <tr key={i} style={{ borderColor: "#f1f5f9" }}>
                        <td style={{ color: "#64748b", paddingLeft: 14, paddingTop: 5, paddingBottom: 5 }}>{row.label}</td>
                        <td
                          className="text-end"
                          style={{
                            paddingRight: 14,
                            paddingTop: 5,
                            paddingBottom: 5,
                            color: row.color,
                            fontWeight: row.bold ? 700 : 500,
                          }}
                        >
                          {row.value}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f0fdf4", borderTop: "2px solid #bbf7d0" }}>
                      <td style={{ paddingLeft: 14, paddingTop: 6, paddingBottom: 6, fontWeight: 700, color: "#065f46" }}>
                        <i className="bi bi-check-circle-fill me-2 text-success" />
                        Total Payable
                      </td>
                      <td
                        className="text-end"
                        style={{ paddingRight: 14, paddingTop: 6, paddingBottom: 6, fontWeight: 800, color: "#059669", fontSize: "0.95rem" }}
                      >
                        {fmt(finalAmount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostEstimator;
